import { forwardRef } from 'react'
import Box, { type BoxProps } from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useHasScrollbars } from 'lib/hooks/useHasScrollbars/useHasScrollbars'
import { mergeRefs } from 'lib/helpers/mergeRefs/mergeRefs'
import { mergeSx } from 'lib/helpers/mergeSx/mergeSx'
import type { WithoutSystemProps } from 'types'
import * as tkn from '../../Modal.tokens'

export interface ModalContentProps extends WithoutSystemProps<BoxProps> {
  dividers?: 'dynamic' | 'static' | 'none'
}

export const ModalContentPaddingX = { xs: 2, md: 3 } as const

export const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>(
  ({ children, dividers = 'dynamic', ...props }, ref) => {
    const isDynamic = dividers === 'dynamic'
    const isStatic = dividers === 'static'

    const [modalContentRef, { vertical: isScrollable }] = useHasScrollbars<HTMLDivElement>({
      skip: !isDynamic,
    })
    const showDividers = isStatic || (isDynamic && isScrollable)

    return (
      <>
        {showDividers && <Divider />}
        <Box
          {...props}
          ref={mergeRefs([modalContentRef, ref])}
          sx={mergeSx(
            {
              flexGrow: 1,
              paddingX: ModalContentPaddingX,
              paddingY: tkn.ModalSpacingContentVerticalDesktop,
              overflowY: 'auto',
            },
            props.sx,
          )}
        >
          {children}
        </Box>
        {showDividers && <Divider />}
      </>
    )
  },
)

ModalContent.displayName = 'ModalContent'
